import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { warningAPI } from '../../services/api';

// ═══════════════════════════════════════════════
// /warning/events — 项目级事件流 (跨所有段)
// ═══════════════════════════════════════════════
const TH = {
  bg: '#FFFFFF', text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  ok: '#1E8449', warn: '#D4770C', critical: '#C0392B', stage: '#2874A6',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

const MODE_LABEL: Record<string, string> = {
  I: 'I 正常延伸', II: 'II 高度增长', IIIa: 'IIIa 砂堵前兆',
  IV: 'IV 砂堵', V: 'V 压窜/破坏',
};
const MODE_COLOR: Record<string, string> = {
  I: '#1E8449', II: '#2874A6', IIIa: '#D4770C', IV: '#C0392B', V: '#6A4C93',
};

type EventRow = {
  id: number; stage_number: number | null; t_s: number | null;
  level: string; mode: string; slope: number | null;
  message: string; suggestion: string;
  acknowledged: boolean; acknowledged_by: string;
  created_at: string;
  run: number | null;
};

export default function WarningEventsPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;

  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState<'all' | 'critical' | 'warning'>('all');
  const [filterAck, setFilterAck] = useState<'all' | 'open' | 'closed'>('open');

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await warningAPI.getEvents(projectId);
      const evs: EventRow[] = Array.isArray(data) ? data : (data as any)?.results ?? [];
      setEvents(evs);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const ack = async (id: number) => {
    try {
      await warningAPI.acknowledge(id);
      message.success('已确认');
      await load();
    } catch { message.error('确认失败'); }
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  const filtered = events.filter((e) => {
    if (filterLevel !== 'all' && e.level !== filterLevel) return false;
    if (filterAck === 'open' && e.acknowledged) return false;
    if (filterAck === 'closed' && !e.acknowledged) return false;
    return true;
  });

  const stats = {
    total: events.length,
    critical: events.filter((e) => e.level === 'critical').length,
    warning: events.filter((e) => e.level === 'warning').length,
    open: events.filter((e) => !e.acknowledged).length,
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${TH.headerBorder}`, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>事件流 (项目级)</h2>
          <span style={{ fontSize: 12, color: TH.textMuted }}>
            共 {stats.total} · 严重 <b style={{ color: TH.critical }}>{stats.critical}</b> ·
            警告 <b style={{ color: TH.warn }}>{stats.warning}</b> ·
            未处理 <b style={{ color: TH.critical }}>{stats.open}</b>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value as any)} style={selStyle}>
            <option value="all">全部等级</option>
            <option value="critical">仅严重</option>
            <option value="warning">仅警告</option>
          </select>
          <select value={filterAck} onChange={(e) => setFilterAck(e.target.value as any)} style={selStyle}>
            <option value="open">未确认</option>
            <option value="closed">已确认</option>
            <option value="all">全部状态</option>
          </select>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
              {['发生时刻', '段', '等级', '模式', '斜率', '事件描述', '处置建议', '状态'].map((h) => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>加载中…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>
                {filterAck === 'open' ? '✓ 没有未确认的事件' : '当前过滤条件下无事件'}
              </td></tr>
            ) : filtered.map((e) => (
              <tr key={e.id} style={{ borderBottom: '1px solid #F0EDE4', opacity: e.acknowledged ? 0.5 : 1 }}>
                <td style={{ padding: '8px 12px', fontFamily: TH.fontMono, fontSize: 11, color: TH.textSec }}>
                  {new Date(e.created_at).toLocaleString('zh-CN')}
                  {e.t_s != null && <div style={{ fontSize: 10, color: TH.textMuted }}>段内 {Math.floor(e.t_s / 60)}m{Math.round(e.t_s % 60)}s</div>}
                </td>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: TH.stage }}>
                  {e.stage_number != null ? `第 ${e.stage_number} 段` : '—'}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{
                    padding: '2px 8px', fontSize: 11, borderRadius: 10,
                    background: e.level === 'critical' ? TH.critical : TH.warn, color: '#fff', fontWeight: 600,
                  }}>{e.level === 'critical' ? '严重' : '警告'}</span>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {e.mode && (
                    <>
                      <span style={{ color: MODE_COLOR[e.mode] || TH.text, fontWeight: 700 }}>{e.mode}</span>
                      <span style={{ color: TH.textMuted, marginLeft: 6, fontSize: 11 }}>
                        {MODE_LABEL[e.mode]?.slice(MODE_LABEL[e.mode].indexOf(' ') + 1)}
                      </span>
                    </>
                  )}
                </td>
                <td style={{ padding: '8px 12px', fontFamily: TH.fontMono }}>{e.slope != null ? e.slope.toFixed(2) : '—'}</td>
                <td style={{ padding: '8px 12px', color: TH.text, maxWidth: 360 }}>{e.message}</td>
                <td style={{ padding: '8px 12px', color: TH.textSec, maxWidth: 280, fontSize: 11 }}>{e.suggestion}</td>
                <td style={{ padding: '8px 12px' }}>
                  {e.acknowledged ? (
                    <span style={{ color: TH.ok, fontSize: 11 }}>✓ {e.acknowledged_by || '已确认'}</span>
                  ) : (
                    <button onClick={() => ack(e.id)} style={{
                      padding: '3px 10px', fontSize: 11, borderRadius: 3,
                      background: TH.stage, color: '#fff', border: 'none', cursor: 'pointer',
                    }}>确认</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const selStyle: React.CSSProperties = {
  fontSize: 12, padding: '4px 8px', borderRadius: 4,
  border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.text,
};
